import React from 'react'
import { Link } from 'app/common/link'
import Responsive from 'react-responsive'
import styled, { css } from 'styled-components'
import { media, ResponsiveContainer } from 'app/assets/styles'
import { colors as oldColors } from 'shared/assets/styles'
import { CaptionText, fonts, H6, TinyText } from 'app/common/typography'
import { Facebook, Instagram, TikTok, Twitter } from 'app/assets/icons'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import type { FooterItemDTO } from 'shared/types'
import { eventsDefinitions } from 'shared/store/metadata'
import { ApplicationDownload } from 'app/common/application-download'
import { marketSelect } from '../utils'

const renderNavigation = (f, isActiveZendesk) => {
    if (!f) {
        return null
    }
    const navigation = f.map((column, index) => {
        const { category, items } = column
        const navItems = items.map((item, index) => {
            const { label, actionUrl, external } = item
            const metaClick = {
                ...eventsDefinitions['FOOTER_LINK'],
                event_action: category,
                event_label: label
            }
            const isFaq = actionUrl === '/service-conso'
            return (
                <li key={index}>
                    <Link
                        external={isFaq && !!isActiveZendesk ? isFaq : external}
                        url={
                            isFaq && !!isActiveZendesk
                                ? process.env.REACT_APP_ZENDESK_URL
                                : actionUrl
                        }
                        meta={metaClick}>
                        <CaptionText color="white">{label}</CaptionText>
                    </Link>
                </li>
            )
        })
        return (
            <Category key={index}>
                <CategoryTitle color="white">{category}</CategoryTitle>
                <ul>{navItems}</ul>
            </Category>
        )
    })
    return <FooterNavigation>{navigation}</FooterNavigation>
}

type Props = {
    footer: Array<FooterItemDTO>,
    copyright: string,
    isActiveZendesk: boolean,
    intl: IntlShape,
    sendNewsletterSubscription: string => void,
    subscriptionSuccess: boolean,
    trySubscription: boolean
}

const Footer = ({ footer, intl, isActiveZendesk }: Props) => {
    let metaFacebook = { ...eventsDefinitions['SOCIAL'] }
    let metaInstagram = { ...eventsDefinitions['SOCIAL'] }
    let metaTwitter = { ...eventsDefinitions['SOCIAL'] }
    let metaTiktok = { ...eventsDefinitions['SOCIAL'] }
    metaFacebook.event_label = 'facebook'
    metaInstagram.event_label = 'instagram'
    metaTiktok.event_label = 'tiktok'
    const page = intl.formatMessage({
        id: 'routes.custom'
    })
    const privacy = `${page}/${intl.formatMessage({
        id: 'routes.privacy'
    })}`

    const socials = marketSelect({
        france: (
            <>
                <SocialIcon
                    url={intl.formatMessage({
                        id: 'social.facebook.url'
                    })}
                    external
                    meta={metaFacebook}>
                    <Facebook />
                </SocialIcon>
                <SocialIcon
                    url={intl.formatMessage({
                        id: 'social.instagram.url'
                    })}
                    external
                    meta={metaInstagram}>
                    <Instagram color="white" />
                </SocialIcon>
                <SocialIcon
                    url={intl.formatMessage({
                        id: 'social.twitter.url'
                    })}
                    external
                    meta={metaTwitter}>
                    <Twitter color="white" />
                </SocialIcon>
                <SocialIcon
                    url={intl.formatMessage({
                        id: 'social.tiktok.url'
                    })}
                    external
                    meta={metaTiktok}>
                    <TikTok color="white" />
                </SocialIcon>
            </>
        ),
        reunion: (
            <>
                <SocialIcon
                    url={intl.formatMessage({
                        id: 'social.facebook.url.re'
                    })}
                    external
                    meta={metaFacebook}>
                    <Facebook />
                </SocialIcon>
                <SocialIcon
                    url={intl.formatMessage({
                        id: 'social.instagram.url.re'
                    })}
                    external
                    meta={metaInstagram}>
                    <Instagram color="white" />
                </SocialIcon>
                <SocialIcon
                    url={intl.formatMessage({
                        id: 'social.tiktok.url.re'
                    })}
                    external
                    meta={metaTiktok}>
                    <TikTok color="white" />
                </SocialIcon>
            </>
        )
    })

    return (
        <FooterContainer>
            <Row>
                <Column>
                    <StyledH6 font={fonts.roboto} color="white">
                        <FormattedMessage id="footer.social.title" />
                    </StyledH6>
                    <SocialIconContainer>{socials}</SocialIconContainer>
                    <StyledH6 font={fonts.roboto} color="white">
                        <FormattedMessage id="footer.applications.title" />
                    </StyledH6>
                    <ApplicationDownload />
                </Column>
            </Row>
            <Responsive minWidth={1024}>
                {largeScreens => {
                    if (largeScreens) {
                        return renderNavigation(footer, isActiveZendesk)
                    } else {
                        return (
                            <FooterNavigation>
                                <Link
                                    url={intl.formatMessage({
                                        id: 'routes.invest'
                                    })}
                                    external>
                                    <CaptionText color="white">
                                        <FormattedMessage id="footer.navigation.invest" />
                                    </CaptionText>
                                </Link>
                            </FooterNavigation>
                        )
                    }
                }}
            </Responsive>
            <Copyright>
                <CopyrightElement>
                    <Link
                        url={`${page}/${intl.formatMessage({
                            id: 'routes.legalNotice'
                        })}`}>
                        <FormattedMessage id="footer.legalNotice">
                            {(text: string) => (
                                <CopyrightText>{text}</CopyrightText>
                            )}
                        </FormattedMessage>
                    </Link>
                </CopyrightElement>
                <CopyrightElement>
                    <Link
                        url={`${page}/${intl.formatMessage({
                            id: 'routes.cgv'
                        })}`}>
                        <FormattedMessage id="footer.cgv">
                            {(text: string) => (
                                <CopyrightText>{text}</CopyrightText>
                            )}
                        </FormattedMessage>
                    </Link>
                </CopyrightElement>
                <CopyrightElement>
                    <Link url={privacy}>
                        <FormattedMessage id="footer.privacy">
                            {(text: string) => (
                                <CopyrightText>{text}</CopyrightText>
                            )}
                        </FormattedMessage>
                    </Link>
                </CopyrightElement>
                <Link
                    url={`${page}/${intl.formatMessage({
                        id: 'routes.cookies'
                    })}`}>
                    <FormattedMessage id="footer.cookies">
                        {(text: string) => (
                            <CopyrightText>{text}</CopyrightText>
                        )}
                    </FormattedMessage>
                </Link>
            </Copyright>
            <EatMove>
                <FormattedMessage id="footer.eatmove.label">
                    {(text: string) => <EatMoveText>{text}</EatMoveText>}
                </FormattedMessage>
                <Link
                    url={intl.formatMessage({
                        id: 'footer.eatmove.link'
                    })}
                    external={true}>
                    <FormattedMessage id="footer.eatmove.link.label">
                        {(text: string) => <EatMoveText>{text}</EatMoveText>}
                    </FormattedMessage>
                </Link>
            </EatMove>
            <EatMove>
                <FormattedMessage
                    id="version"
                    values={{ version: process.env.REACT_APP_VERSION }}>
                    {(text: string) => (
                        <TinyText center color={oldColors.grey}>
                            {text}
                        </TinyText>
                    )}
                </FormattedMessage>
            </EatMove>
        </FooterContainer>
    )
}

const borderMixin = css`
    border-top: solid 1px rgba(102, 102, 102, 0.6);
`

const FooterContainer = styled.footer`
    background-color: #2b0200;
    z-index: 1000;
    a {
        text-decoration: underline;
    }
`

const FooterNavigation = styled(ResponsiveContainer)`
    ${borderMixin};
    display: flex;
    justify-content: center;
    margin: auto;
    padding: 30px 0;
`

const Copyright = styled(ResponsiveContainer)`
    ${borderMixin};
    padding: 16px 0;
    text-align: center;
`

const CategoryTitle = styled(H6)`
    margin-bottom: 8px;
    margin-top: 0;
`

const Category = styled.nav`
    min-width: 156px;
    margin: 0 8px;
`

const SocialIconContainer = styled.div`
    margin-top: 10px;
    margin-bottom: 30px;
`

const SocialIcon = styled(Link)`
    margin-right: 20px;
`

const StyledH6 = styled(H6)`
    text-transform: none;
    margin: 0;
`

const Row = styled(ResponsiveContainer)`
    display: flex;
    flex-direction: column;
    ${media.medium`
        flex-direction: row;
    `};
`

const Column = styled.div`
    box-sizing: border-box;
    padding: 30px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    &:last-of-type {
        ${borderMixin};
    }
    ${media.medium`
        align-items: flex-start;
        justify-content: flex-start;
        padding: 40px 0;
        margin-left: 108px;
        width: 50%;
        &:last-of-type {
            border: none;
        }
    `};
`

const CopyrightText = styled(CaptionText)`
    color: white;
    text-decoration: underline;
`

const EatMove = styled(ResponsiveContainer)`
    padding-bottom: 16px;
    text-align: center;
`

const EatMoveText = styled(CaptionText)`
    color: white;
`

const CopyrightElement = styled(CaptionText)`
    display: block;
    ${media.medium`
        display: inline;
        &:after {
            content: ' | ';
        }
    `};
`

export const FooterComponent = injectIntl(Footer)
